import { Moment } from 'moment';
import cn from 'classnames';
import {
  BadgeCell,
  BadgeCellComponentProps,
  ChipsCell,
  ChipsCellComponentProps,
  ChipsSkeleton,
  Column,
  DropdownFilterOption,
  FilterItems,
} from '@odin-labs/components';
import type { JobsiteAccessSessionsInput } from 'apollo/generated/client-operations';
import { getISODateTimeToFormattedDate } from 'utils/dates';
import {
  SessionDateCell,
  SessionDateCellData,
} from 'containers/jobsiteAccessActivity/helpers/cells';
import {
  JobsiteAccessSession,
  WorkerJobsiteAccessSessionsFilters,
  WorkerJobsiteAccessSessionsFiltersOptions,
} from './types';

export type JobsiteAccessSessionColumn = Column<JobsiteAccessSession>;

// A session the worker has not badged out of yet is not non-compliant until it
// stops being in progress, which the API works out (see inProgress).
const getSessionStatusBadge = (
  session: JobsiteAccessSession,
): BadgeCellComponentProps => {
  if (session.inProgress) {
    return { text: 'In Progress', color: 'yellow', size: 'lg' };
  }
  const compliant = session.proximitySession?.compliant;
  return {
    text: compliant ? 'Compliant' : 'Non-Compliant',
    color: compliant ? 'green' : 'red',
    size: 'lg',
  };
};

export const getColumns = (): JobsiteAccessSessionColumn[] => [
  {
    id: 'jobsiteContractor',
    Header: 'Jobsite / Contractor',
    accessor: (): string[] => [],
    Cell: ChipsCell,
    CellLoading: ChipsSkeleton,
    componentProps: (
      session: JobsiteAccessSession,
    ): ChipsCellComponentProps => ({
      chips: [
        {
          text: session?.jobsiteWorker?.jobsiteContractor.jobsite.name,
          secondaryText:
            session.jobsiteWorker?.contractorWorker.contractor.organization
              .name,
          isActive: session.jobsiteWorker?.currentAccess.isAllowed,
        },
      ],
    }),
  },
  {
    id: 'date',
    Header: 'Date',
    accessor: (session: JobsiteAccessSession): SessionDateCellData => ({
      date: getISODateTimeToFormattedDate(session?.proximitySession?.day),
      isOvernight: session?.overnightSession ?? false,
    }),
    Cell: SessionDateCell,
  },
  {
    id: 'start',
    Header: 'Start',
    accessor: (session: JobsiteAccessSession): string =>
      session.proximitySession?.locale?.time?.start,
  },
  {
    id: 'end',
    Header: 'End',
    accessor: (session: JobsiteAccessSession): string =>
      session.proximitySession?.locale?.time?.end,
  },
  {
    id: 'totalHoursWorked',
    Header: 'Total Hours Worked',
    accessor: (session: JobsiteAccessSession): string =>
      session.proximitySession?.span?.hours?.toFixed(2) ?? '0',
  },
  {
    id: 'status',
    Header: 'Status',
    Cell: BadgeCell,
    componentProps: getSessionStatusBadge,
  },
];

// The status filter matches the status badge: a session in progress is
// neither compliant nor non-compliant yet.
export const sessionStatusOptions: DropdownFilterOption[] = [
  {
    value: 'inProgress',
    label: 'In Progress',
  },
  {
    value: 'compliant',
    label: 'Compliant',
  },
  {
    value: 'nonCompliant',
    label: 'Non-Compliant',
  },
];

export const getSessionStatusInput = (
  sessionStatus: string,
): Pick<JobsiteAccessSessionsInput, 'compliant' | 'inProgress'> => {
  switch (sessionStatus) {
    case 'inProgress':
      return { compliant: null, inProgress: true };
    case 'compliant':
      return { compliant: true, inProgress: false };
    case 'nonCompliant':
      return { compliant: false, inProgress: false };
    default:
      return { compliant: null, inProgress: null };
  }
};

export const getFilterItems = ({
  filtersOptions,
  jobsiteIds,
  contractorIds,
  sessionStatus,
  startDate,
  endDate,
}: {
  filtersOptions: WorkerJobsiteAccessSessionsFiltersOptions;
  jobsiteIds: string[];
  contractorIds: string[];
  sessionStatus: string;
  startDate: Moment | string;
  endDate: Moment | string;
}): FilterItems<WorkerJobsiteAccessSessionsFilters> => ({
  dateRange: {
    header: 'Date',
    type: 'datePicker',
    defaultValue: { startDate, endDate },
    layout: cn('odin-col-span-full'),
    componentProps: {
      isRange: true,
    },
  },
  jobsiteIds: {
    header: 'Jobsite',
    type: 'dropdown',
    defaultValue: jobsiteIds,
    componentProps: {
      options: filtersOptions?.jobsites ?? [],
    },
  },
  contractorIds: {
    header: 'Contractor',
    type: 'dropdown',
    defaultValue: contractorIds,
    componentProps: {
      options: filtersOptions?.contractors ?? [],
      isVirtual: true,
    },
  },
  sessionStatus: {
    header: 'Status',
    type: 'dropdown',
    defaultValue: sessionStatus,
    componentProps: {
      options: sessionStatusOptions,
      isMulti: false,
    },
  },
});
